import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, PaperPlaneTilt, Trash, CheckCircle, ClockCounterClockwise, Star } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";

/**
 * References section for a candidate (V1.3, Feb 2026).
 *
 * Recruiter-facing view. Add up to 3 references, send a survey link to
 * each referee, and see their responses inline once submitted.
 * Public referee-facing form lives in `PublicReferencePage.jsx`.
 */
export default function ReferencesSection({ candidateId }) {
    const [state, setState] = useState({ loading: true, refs: [], max: 3 });
    const [showAdd, setShowAdd] = useState(false);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({ name: "", email: "", phone: "", relationship: "" });

    const load = async () => {
        setState((s) => ({ ...s, loading: true }));
        try {
            const r = await api.get(`/candidates/${candidateId}/references`);
            setState({ loading: false, refs: r.data.references || [], max: r.data.max || 3 });
        } catch (ex) {
            toast.error(formatApiError(ex) || "Could not load references.");
            setState({ loading: false, refs: [], max: 3 });
        }
    };
    useEffect(() => { if (candidateId) load(); }, [candidateId]);   // eslint-disable-line

    const add = async (e) => {
        e.preventDefault();
        if (saving) return;
        setSaving(true);
        try {
            await api.post(`/candidates/${candidateId}/references`, {
                name: form.name.trim(),
                email: form.email.trim().toLowerCase(),
                phone: form.phone.trim() || null,
                relationship: form.relationship.trim(),
            });
            toast.success("Reference added.");
            setForm({ name: "", email: "", phone: "", relationship: "" });
            setShowAdd(false);
            load();
        } catch (ex) {
            toast.error(formatApiError(ex) || "Could not add reference.");
        } finally {
            setSaving(false);
        }
    };

    const send = async (rid) => {
        try {
            await api.post(`/candidates/${candidateId}/references/${rid}/send`);
            toast.success("Survey emailed.");
            load();
        } catch (ex) {
            toast.error(formatApiError(ex) || "Could not send survey.");
        }
    };

    const remove = async (rid) => {
        if (!confirm("Remove this reference? This cannot be undone.")) return;
        try {
            await api.delete(`/candidates/${candidateId}/references/${rid}`);
            toast.success("Removed.");
            load();
        } catch (ex) {
            toast.error(formatApiError(ex) || "Could not remove.");
        }
    };

    const atCap = state.refs.length >= state.max;

    return (
        <div data-testid="references-section" className="space-y-3">
            <div className="flex items-center justify-between">
                <div>
                    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#A3A3A3]">
                        References · {state.refs.length} of {state.max}
                    </p>
                    <p className="text-xs text-[#525252] mt-1">
                        Add up to {state.max} references. Each gets a 5-question survey link over email.
                    </p>
                </div>
                {!showAdd && !atCap && (
                    <button
                        type="button"
                        data-testid="add-reference-btn"
                        onClick={() => setShowAdd(true)}
                        className="inline-flex items-center gap-1.5 border border-[#E5E5E5] hover:border-ink px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider transition-colors"
                    >
                        <Plus size={12} weight="bold" /> Add reference
                    </button>
                )}
            </div>

            {showAdd && (
                <form onSubmit={add} className="border border-ink p-4 space-y-3" data-testid="add-reference-form">
                    <div className="grid grid-cols-2 gap-3">
                        <label className="block col-span-2">
                            <span className="block text-[10px] font-black uppercase tracking-wider text-[#525252] mb-1">Referee name</span>
                            <input
                                data-testid="ref-name"
                                required
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                className="w-full border border-[#E5E5E5] focus:border-ink outline-none px-3 py-2 text-sm"
                            />
                        </label>
                        <label className="block">
                            <span className="block text-[10px] font-black uppercase tracking-wider text-[#525252] mb-1">Email</span>
                            <input
                                data-testid="ref-email"
                                type="email"
                                required
                                value={form.email}
                                onChange={(e) => setForm({ ...form, email: e.target.value })}
                                className="w-full border border-[#E5E5E5] focus:border-ink outline-none px-3 py-2 text-sm"
                            />
                        </label>
                        <label className="block">
                            <span className="block text-[10px] font-black uppercase tracking-wider text-[#525252] mb-1">Phone (optional)</span>
                            <input
                                data-testid="ref-phone"
                                value={form.phone}
                                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                                className="w-full border border-[#E5E5E5] focus:border-ink outline-none px-3 py-2 text-sm"
                            />
                        </label>
                        <label className="block col-span-2">
                            <span className="block text-[10px] font-black uppercase tracking-wider text-[#525252] mb-1">Relationship</span>
                            <input
                                data-testid="ref-relationship"
                                required
                                placeholder="e.g. Direct manager at Acme"
                                value={form.relationship}
                                onChange={(e) => setForm({ ...form, relationship: e.target.value })}
                                className="w-full border border-[#E5E5E5] focus:border-ink outline-none px-3 py-2 text-sm"
                            />
                        </label>
                    </div>
                    <div className="flex items-center justify-end gap-2">
                        <button
                            type="button"
                            onClick={() => setShowAdd(false)}
                            className="border border-[#E5E5E5] hover:border-ink text-[#525252] hover:text-ink px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            data-testid="ref-save-btn"
                            disabled={saving}
                            className="bg-ink hover:bg-cobalt disabled:opacity-60 text-white px-4 py-1.5 text-[11px] font-black uppercase tracking-wider transition-colors"
                        >
                            {saving ? "Saving…" : "Save reference"}
                        </button>
                    </div>
                </form>
            )}

            {state.loading && (
                <p className="text-xs text-[#A3A3A3]">Loading references…</p>
            )}
            {!state.loading && state.refs.length === 0 && (
                <div className="border border-dashed border-[#E5E5E5] p-6 text-center">
                    <p className="text-sm text-[#525252]">No references yet.</p>
                    <p className="text-xs text-[#A3A3A3] mt-1">Add up to {state.max} referees to collect structured feedback.</p>
                </div>
            )}
            {state.refs.map((r) => (
                <ReferenceRow
                    key={r.id}
                    row={r}
                    onSend={() => send(r.id)}
                    onDelete={() => remove(r.id)}
                />
            ))}
        </div>
    );
}

function ReferenceRow({ row, onSend, onDelete }) {
    const statusChip = row.status === "submitted"
        ? { icon: <CheckCircle size={12} weight="fill" className="text-emerald-600" />, label: "Submitted", cls: "text-emerald-800 bg-emerald-50 border-emerald-200" }
        : row.status === "sent"
            ? { icon: <ClockCounterClockwise size={12} weight="bold" className="text-amber-700" />, label: "Awaiting", cls: "text-amber-800 bg-amber-50 border-amber-200" }
            : { icon: null, label: "Draft", cls: "text-[#525252] bg-[#F5F5F5] border-[#E5E5E5]" };

    return (
        <div
            data-testid={`reference-row-${row.id}`}
            className="border border-[#E5E5E5] p-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between"
        >
            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-bold text-sm text-ink truncate">{row.name}</p>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 border text-[10px] font-black uppercase tracking-wider ${statusChip.cls}`}>
                        {statusChip.icon} {statusChip.label}
                    </span>
                </div>
                <p className="text-xs text-[#525252] truncate">{row.email}{row.phone ? ` · ${row.phone}` : ""}</p>
                <p className="text-[11px] text-[#A3A3A3] mt-0.5">{row.relationship}</p>
                {row.responses && (
                    <ResponseSummary responses={row.responses} submitted_at={row.submitted_at} />
                )}
            </div>
            {row.status !== "submitted" && (
                <div className="flex items-center gap-1 shrink-0">
                    <button
                        type="button"
                        data-testid={`ref-send-${row.id}`}
                        onClick={onSend}
                        className="inline-flex items-center gap-1 bg-ink hover:bg-cobalt text-white px-3 py-1.5 text-[10px] font-black uppercase tracking-wider transition-colors"
                        title={row.status === "sent" ? "Re-send survey email" : "Send survey email"}
                    >
                        <PaperPlaneTilt size={11} weight="bold" />
                        {row.status === "sent" ? "Re-send" : "Send"}
                    </button>
                    <button
                        type="button"
                        data-testid={`ref-delete-${row.id}`}
                        onClick={onDelete}
                        className="p-1.5 border border-[#E5E5E5] hover:border-rose-400 hover:text-rose-600 text-[#525252]"
                        title="Remove reference"
                    >
                        <Trash size={12} weight="bold" />
                    </button>
                </div>
            )}
        </div>
    );
}

function ResponseSummary({ responses, submitted_at }) {
    const stars = responses.overall_rating || 0;
    return (
        <div className="mt-3 border-l-2 border-emerald-400 pl-3 space-y-2">
            <div className="flex items-center gap-2">
                <div className="flex items-center">
                    {[1, 2, 3, 4, 5].map((n) => (
                        <Star key={n} size={12} weight={stars >= n ? "fill" : "regular"} className={stars >= n ? "text-amber-500" : "text-[#D4D4D4]"} />
                    ))}
                </div>
                <span className="text-[11px] text-[#525252] font-bold">{stars}/5</span>
                <span className="text-[10px] text-[#A3A3A3]">·</span>
                <span className="text-[10px] text-[#525252]">
                    Recommend: <b>{(responses.recommend_for_role || "—").toUpperCase()}</b>
                </span>
                <span className="text-[10px] text-[#A3A3A3]">·</span>
                <span className="text-[10px] text-[#525252]">
                    Rehire: <b>{(responses.would_rehire || "—").toUpperCase()}</b>
                </span>
            </div>
            {responses.strongest_skills && (
                <p className="text-[11px] text-[#525252]">
                    <span className="font-bold">Strengths:</span> {responses.strongest_skills}
                </p>
            )}
            {responses.improvement_areas && (
                <p className="text-[11px] text-[#525252]">
                    <span className="font-bold">Improve:</span> {responses.improvement_areas}
                </p>
            )}
            {responses.recommend_comment && (
                <p className="text-[11px] text-[#525252] italic">
                    &ldquo;{responses.recommend_comment}&rdquo;
                </p>
            )}
            {submitted_at && (
                <p className="text-[10px] text-[#A3A3A3]">
                    Submitted {new Date(submitted_at).toLocaleString()}
                </p>
            )}
        </div>
    );
}
